import { ArrowForwardIosSharp, Edit } from '@mui/icons-material';
import { Grid, Typography, Paper, Accordion, AccordionDetails, AccordionSummary, Button, Skeleton, Stack, Alert } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { Sending } from 'src/modules/invoice/models/invoiceTemplate';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Colors } from 'src/shared/components/styles/colors';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { AdminInvoiceTemplateSkeleton } from './adminInvoiceTemplateSkeleton';
import { CreateInvoiceTemplatePreviewRequest } from '../models/createInvoiceTemplatePreviewRequest';
import { PreviewInvoiceTemplateDownloader } from './previewInvoiceTemplateDownloader';

type RenderInvoiceTemplateSendingProps = {
  loading: boolean;
  setLoading: (pdfLoader: boolean) => void;
  sendingItem: Sending;
  index: number;
};

export const RenderInvoiceTemplateSending = (props: RenderInvoiceTemplateSendingProps) => {
  const { loading, setLoading, sendingItem, index } = props;
  const [edit, setEdit] = useState<boolean>(false);

  const dataToPdf: CreateInvoiceTemplatePreviewRequest = {
    templateSendingsId: sendingItem.templateSendings_id,
  };

  const renderContent = () => {
    switch (sendingItem.sendingType) {
      case InvoiceSendingTypeIntegerEnum.Email:
        return {
          name: <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceTemplatePage.emailSending')}</Typography>,
          content: <Typography sx={{ fontWeight: 500, color: 'text.secondary' }}>{t('invoice:adminInvoiceTemplatePage.emailContent')}</Typography>,
          subject: (
            <ControlledTextField
              sx={{ backgroundColor: Colors.White }}
              disabled={!edit}
              name={`sendings[${index}].emailHeading`}
              label={t('invoice:createMemberInvoice.heading')}
            />
          ),
          body: <ControlledTextEditor disabled={!edit} rows={30} name={`sendings[${index}].emailText`} />,
        };
      case InvoiceSendingTypeIntegerEnum.Main:
        return {
          name: <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceTemplatePage.mainSending')}</Typography>,
          content: (
            <Typography sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>{t('invoice:adminInvoiceTemplatePage.invoiceContent')}</Typography>
          ),
          subject: (
            <ControlledTextField
              sx={{ backgroundColor: Colors.White }}
              disabled={!edit}
              name={`sendings[${index}].defaultHeading`}
              label={t('invoice:createMemberInvoice.heading')}
            />
          ),
          body: <ControlledTextEditor disabled={!edit} rows={30} name={`sendings[${index}].defaultText`} />,
        };
      case InvoiceSendingTypeIntegerEnum.Reminder1:
        return {
          name: <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceTemplatePage.reminderOne')}</Typography>,
          content: (
            <Typography sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>{t('invoice:adminInvoiceTemplatePage.invoiceContent')}</Typography>
          ),
          subject: (
            <ControlledTextField
              sx={{ backgroundColor: Colors.White }}
              disabled={!edit}
              name={`sendings[${index}].defaultHeading`}
              label={t('invoice:createMemberInvoice.heading')}
            />
          ),
          body: <ControlledTextEditor disabled={!edit} rows={30} name={`sendings[${index}].defaultText`} />,
        };
      case InvoiceSendingTypeIntegerEnum.Reminder2:
        return {
          name: <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceTemplatePage.reminderTwo')}</Typography>,
          content: (
            <Typography sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>{t('invoice:adminInvoiceTemplatePage.invoiceContent')}</Typography>
          ),
          subject: (
            <ControlledTextField
              sx={{ backgroundColor: Colors.White }}
              disabled={!edit}
              name={`sendings[${index}].defaultHeading`}
              label={t('invoice:createMemberInvoice.heading')}
            />
          ),
          body: <ControlledTextEditor disabled={!edit} rows={30} name={`sendings[${index}].defaultText`} />,
        };
      default:
        return { subject: null, body: null };
    }
  };

  const { name, content, subject, body } = renderContent();

  return (
    <>
      <Accordion disableGutters elevation={0}>
        <AccordionSummary
          expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
          aria-controls={`panel-${sendingItem.templateSendings_id}-content`}
          id={`panel-${sendingItem.templateSendings_id}-header`}
        >
          <Typography sx={{ fontWeight: 500 }}>{name}</Typography>
          {(sendingItem.notReady || sendingItem.textApproved === false) && (
            <Stack direction='row' spacing={1} ml={1.5} sx={{ '& .MuiChip-filled ': { borderRadius: 1 } }}>
              <CustomChip label={t('invoice:adminInvoiceTemplatePage.notApproved')} color='error' size='small' />
            </Stack>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            {(sendingItem.notReady || sendingItem.textApproved === false) && (
              <Alert sx={{ marginLeft: 5 }} severity='error'>
                {t('invoice:adminInvoiceTemplatePage.invoiceTemplateNotApproved')}
              </Alert>
            )}
            <Grid item xs={12} px={5}>
              <Grid container mt={1}>
                <Paper
                  sx={{
                    border: '2px solid, lightGrey',
                    background: '#F5F5F5',
                    boxShadow: 4,
                  }}
                >
                  <Grid container px={2} mt={1}>
                    {content}
                    {sendingItem.sendingType !== InvoiceSendingTypeIntegerEnum.Email && (
                      <Grid item xs={12} mb={1}>
                        <AdminInvoiceTemplateSkeleton />
                      </Grid>
                    )}
                    <Grid item xs={12} mt={4} mb={3}>
                      {subject}
                    </Grid>
                    {sendingItem.sendingType !== InvoiceSendingTypeIntegerEnum.Email && (
                      <>
                        <Grid item xs={9} mb={2}>
                          <Typography variant='body2' color='textSecondary'>
                            {t('invoice:createMemberInvoice.memberNumber')}
                          </Typography>
                          <Skeleton variant='text' animation={false} sx={{ height: '20px', width: '90%' }} />
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant='body2' color='textSecondary'>
                            {t('invoice:createMemberInvoice.fee')}
                          </Typography>
                          <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '95%' }} />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} mb={2}>
                      <LoadingOverlay loading={loading} />
                      {body}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <Grid container mt={3} justifyContent='space-between' alignItems='center'>
              <Grid item xs='auto' mb={{ xs: 1, sm: 0 }}>
                <Button
                  variant='outlined'
                  disabled={loading}
                  onClick={() => PreviewInvoiceTemplateDownloader({ dataToPdf, loadingCallback: setLoading })}
                >
                  {t('invoice:createMemberInvoice.previewInvoice')}
                </Button>
              </Grid>
              <Grid item xs='auto'>
                <Button
                  size='medium'
                  variant='outlined'
                  endIcon={<Edit fontSize='small' />}
                  sx={{ mr: 2 }}
                  disabled={edit || loading}
                  onClick={() => setEdit(true)}
                >
                  {t('common:button.edit')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
